import React, { useState, createContext, Suspense, lazy } from 'react';
import { 
  createBrowserRouter,
  RouterProvider
} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/auth/PrivateRoute';
import Layout from './components/layout/Layout';
import { useAuth } from './contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import CookieConsent from './components/cookies/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import RouteError from './components/RouteError';

// Lazy load heavy components for code splitting
const ItemManagement = lazy(() => import('./components/items/ItemManagement'));
const WasteTabs = lazy(() => import('./components/waste/WasteTabs'));
const Profile = lazy(() => import('./components/profile/Profile'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const SoftLaunchPage = lazy(() => import('./pages/SoftLaunchPage'));
const BarcodeScannerTestPage = lazy(() => import('./components/barcode/BarcodeScannerTestPage'));

// Lazy load public pages (less critical, can load on demand)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '400px' 
    }}
  >
    <CircularProgress />
  </Box>
);

// Create a green-themed Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32', // Green 800
      light: '#4caf50', // Green 500
      dark: '#1b5e20', // Green 900
    },
    secondary: {
      main: '#81c784', // Green 300
      light: '#a5d6a7', // Green 200
      dark: '#66bb6a', // Green 400
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

export const ExpiryNotificationContext = createContext();

// Component to redirect logged-in users to /app/privacy, or show public privacy policy
function PrivacyRedirect() {
  const { currentUser } = useAuth();
  
  if (currentUser) {
    return <Navigate to="/app/privacy" replace />;
  }
  
  return (
    <Suspense fallback={<LoadingFallback />}>
      <PrivacyPolicy />
    </Suspense>
  );
}

function App() {
  const [expiryNotification, setExpiryNotification] = useState({
    expired: 0,
    expiring: 0,
    expiredItems: [],
    expiringItems: []
  });
  const router = createBrowserRouter([
    {
      errorElement: <RouteError />,
      children: [
    {
      path: '/',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LandingPage />
        </Suspense>
      )
    },
    {
      path: '/login',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      )
    },
    {
      path: '/register',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <RegisterPage />
        </Suspense>
      )
    },
    {
      path: '/reset-password',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <ResetPasswordPage />
        </Suspense>
      )
    },
    {
      path: '/privacy',
      element: <PrivacyRedirect />
    },
    {
      path: '/terms',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <TermsOfService />
        </Suspense>
      )
    },
    {
      path: '/contact',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <ContactPage />
        </Suspense>
      )
    },
    {
      path: '/help',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <HelpPage />
        </Suspense>
      )
    },
    {
      path: '/app',
      element: (
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      ),
      children: [
        {
          path: 'profile',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Profile />
            </Suspense>
          )
        },
        {
          path: 'items',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ItemManagement setExpiryNotification={setExpiryNotification} />
            </Suspense>
          )
        },
        {
          path: 'waste',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WasteTabs />
            </Suspense>
          )
        },
        {
          path: 'community',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CommunityPage />
            </Suspense>
          )
        },
        {
          path: 'admin',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SoftLaunchPage />
            </Suspense>
          )
        },
        {
          path: 'privacy',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PrivacyPolicy />
            </Suspense>
          )
        },
        {
          path: 'terms',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TermsOfService />
            </Suspense>
          )
        },
        {
          path: 'contact',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ContactPage />
            </Suspense>
          )
        },
        {
          path: 'help',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <HelpPage />
            </Suspense>
          )
        },
        {
          path: 'test-barcode',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BarcodeScannerTestPage />
            </Suspense>
          )
        }
      ]
    }
      ]
    }
  ], {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  });
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ExpiryNotificationContext.Provider value={{ expiryNotification, setExpiryNotification }}>
            <RouterProvider router={router} />
            <CookieConsent />
          </ExpiryNotificationContext.Provider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 